import { makeAuthenticatedRequest } from './authService';

export interface SocialPost {
  id: number;
  user_id: number;
  content: string;
  created_at: string;
  updated_at?: string;
  username?: string;
  display_name?: string;
  profile_picture_url?: string;
  likes_count?: number;
  comments_count?: number;
  is_liked?: boolean;
}

export interface SocialComment {
  id: number;
  post_id: number;
  user_id: number;
  parent_comment_id?: number | null;
  content: string;
  created_at: string;
  updated_at?: string;
  username?: string;
  display_name?: string;
  profile_picture_url?: string;
  replies_count?: number;
  ai_character_id?: number | null;
  ai_character_name?: string | null;
}

export interface CreateCommentData {
  content: string;
  parent_comment_id?: number;
}

export interface UpdateCommentData {
  content: string;
}

export interface CreatePostData {
  content: string;
}

export interface UpdatePostData {
  content: string;
}

class SocialService {
  /**
   * Get all social posts (feed)
   */
  async getPosts(limit: number = 50, offset: number = 0): Promise<SocialPost[]> {
    try {
      const response = await makeAuthenticatedRequest(
        `/social/posts?limit=${limit}&offset=${offset}`
      );
      return response.data || [];
    } catch (error) {
      console.error('Error fetching posts:', error);
      throw error;
    }
  }

  /**
   * Get a specific post by ID
   */
  async getPostById(id: number): Promise<SocialPost> {
    try {
      const response = await makeAuthenticatedRequest(`/social/posts/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching post:', error);
      throw error;
    }
  }

  /**
   * Create a new post
   */
  async createPost(postData: CreatePostData): Promise<SocialPost> {
    try {
      const response = await makeAuthenticatedRequest('/social/posts', {
        method: 'POST',
        body: JSON.stringify(postData)
      });
      return response.data;
    } catch (error) {
      console.error('Error creating post:', error);
      throw error;
    }
  }

  /**
   * Update an existing post
   */
  async updatePost(id: number, postData: UpdatePostData): Promise<SocialPost> {
    try {
      const response = await makeAuthenticatedRequest(`/social/posts/${id}`, {
        method: 'PUT',
        body: JSON.stringify(postData)
      });
      return response.data;
    } catch (error) {
      console.error('Error updating post:', error);
      throw error;
    }
  }

  /**
   * Delete a post
   */
  async deletePost(id: number): Promise<void> {
    try {
      await makeAuthenticatedRequest(`/social/posts/${id}`, {
        method: 'DELETE'
      });
    } catch (error) {
      console.error('Error deleting post:', error);
      throw error;
    }
  }

  /**
   * Like a post
   */
  async likePost(id: number): Promise<void> {
    try {
      await makeAuthenticatedRequest(`/social/posts/${id}/like`, {
        method: 'POST'
      });
    } catch (error) {
      console.error('Error liking post:', error);
      throw error;
    }
  }

  /**
   * Unlike a post
   */
  async unlikePost(id: number): Promise<void> {
    try {
      await makeAuthenticatedRequest(`/social/posts/${id}/like`, {
        method: 'DELETE'
      });
    } catch (error) {
      console.error('Error unliking post:', error);
      throw error;
    }
  }

  /**
   * Get posts by a specific user
   */
  async getUserPosts(userId: number, limit: number = 50, offset: number = 0): Promise<SocialPost[]> {
    try {
      const response = await makeAuthenticatedRequest(
        `/social/users/${userId}/posts?limit=${limit}&offset=${offset}`
      );
      return response.data || [];
    } catch (error) {
      console.error('Error fetching user posts:', error);
      throw error;
    }
  }

  /**
   * Get comments for a post
   */
  async getComments(postId: number, limit: number = 50, offset: number = 0): Promise<SocialComment[]> {
    try {
      const response = await makeAuthenticatedRequest(
        `/social/posts/${postId}/comments?limit=${limit}&offset=${offset}`
      );
      return response.data || [];
    } catch (error) {
      console.error('Error fetching comments:', error);
      throw error;
    }
  }

  /**
   * Create a comment on a post
   */
  async createComment(postId: number, commentData: CreateCommentData): Promise<SocialComment> {
    try {
      const response = await makeAuthenticatedRequest(`/social/posts/${postId}/comments`, {
        method: 'POST',
        body: JSON.stringify(commentData)
      });
      return response.data;
    } catch (error) {
      console.error('Error creating comment:', error);
      throw error;
    }
  }

  /**
   * Update a comment
   */
  async updateComment(postId: number, commentId: number, commentData: UpdateCommentData): Promise<SocialComment> {
    try {
      const response = await makeAuthenticatedRequest(`/social/posts/${postId}/comments/${commentId}`, {
        method: 'PUT',
        body: JSON.stringify(commentData)
      });
      return response.data;
    } catch (error) {
      console.error('Error updating comment:', error);
      throw error;
    }
  }

  /**
   * Delete a comment
   */
  async deleteComment(postId: number, commentId: number): Promise<void> {
    try {
      await makeAuthenticatedRequest(`/social/posts/${postId}/comments/${commentId}`, {
        method: 'DELETE'
      });
    } catch (error) {
      console.error('Error deleting comment:', error);
      throw error;
    }
  }

  /**
   * Get replies for a comment
   */
  async getReplies(postId: number, commentId: number, limit: number = 50, offset: number = 0): Promise<SocialComment[]> {
    try {
      const response = await makeAuthenticatedRequest(
        `/social/posts/${postId}/comments/${commentId}/replies?limit=${limit}&offset=${offset}`
      );
      return response.data || [];
    } catch (error) {
      console.error('Error fetching replies:', error);
      throw error;
    }
  }

  /**
   * Fetch URL preview metadata
   */
  async fetchUrlPreview(url: string): Promise<URLPreview | null> {
    try {
      const response = await makeAuthenticatedRequest(
        `/social/url-preview?url=${encodeURIComponent(url)}`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching URL preview:', error);
      return null;
    }
  }
}

export interface URLPreview {
  url: string;
  title: string;
  description: string;
  image: string;
  siteName: string;
  domain: string;
}

export const socialService = new SocialService();

